/**
 * Detect rasters that have no earth coordinate system so GeoViewer can
 * prompt the operator and offer the Georeference tool. Layers that already
 * carry a real CRS are left completely alone.
 */

import type { Layer } from '@geosoft/viewer-sdk';
import {
  bboxFromCorners,
  readGeoreference,
  type LayerGeoreference,
} from './georeferenceTypes';

const UNUSABLE_CRS = new Set(['', 'LOCAL', 'UNKNOWN', 'EPSG:UNKNOWN', 'EPSG:0']);

function coordinateModeOf(layer: Layer): string | null {
  return typeof layer.style?.coordinateMode === 'string' ? layer.style.coordinateMode : null;
}

function normalizedCrs(layer: Layer): string {
  return (layer.crs ?? layer.sourceCrs ?? '').trim().toUpperCase();
}

/**
 * True when the layer is not earth-referenced. Point clouds / BIM local
 * frames are included in the predicate but the missing-CRS prompt is raster
 * only — those families already have their own placement UX.
 */
export function layerLacksEarthCrs(layer: Layer): boolean {
  const geo = readGeoreference(layer);
  if (geo?.status === 'applied') return false;

  const mode = coordinateModeOf(layer);
  if (mode === 'georeferenced') return false;
  if (mode === 'unknown-local' || mode === 'local-engineering') return true;
  if (layer.style?.hasExplicitCrs === false) return true;
  return UNUSABLE_CRS.has(normalizedCrs(layer));
}

export function isGeoreferenceableKind(layer: Layer): boolean {
  return layer.kind === 'raster' || layer.kind === 'terrain';
}

/** Show the ArcGIS-style “unable to determine earth reference” dialog. */
export function layerShouldPromptMissingCrs(layer: Layer): boolean {
  if (!isGeoreferenceableKind(layer)) return false;
  if (!layerLacksEarthCrs(layer)) return false;
  const status = readGeoreference(layer)?.status;
  return status !== 'fake-coordinates' && status !== 'applied' && status !== 'rectifying';
}

/** Georeference section in the raster ToolCard. Hidden for already-referenced data. */
export function shouldShowGeoreferenceTools(layer: Layer | null | undefined): boolean {
  if (!layer || !isGeoreferenceableKind(layer)) return false;
  const geo = readGeoreference(layer);
  if (geo?.status === 'applied' || geo?.status === 'rectifying' || geo?.status === 'fake-coordinates') {
    return true;
  }
  return layerLacksEarthCrs(layer);
}

export function withGeoreferenceStyle(
  layer: Layer,
  georeference: LayerGeoreference,
  extras: Partial<Layer> = {},
): Layer {
  return {
    ...layer,
    ...extras,
    style: {
      ...layer.style,
      georeference,
      ...(georeference.status === 'applied'
        ? { coordinateMode: 'georeferenced', hasExplicitCrs: true, hasExplicitBounds: true }
        : {}),
    },
  };
}

/** Re-apply a saved georeference when a scene item is hydrated into a Layer. */
export function applyStoredGeoreferenceToLayer(layer: Layer): Layer {
  const geo = readGeoreference(layer);
  if (geo?.status !== 'applied') return layer;
  const bbox = geo.bbox
    ? ([geo.bbox[0], geo.bbox[1], geo.bbox[2], geo.bbox[3]] as [number, number, number, number])
    : bboxFromCorners(geo.corners);
  return {
    ...layer,
    crs: geo.targetCrs ?? 'EPSG:4326',
    sourceCrs: geo.targetCrs ?? layer.sourceCrs,
    bbox: bbox ?? layer.bbox,
    style: {
      ...layer.style,
      coordinateMode: 'georeferenced',
      hasExplicitCrs: true,
      hasExplicitBounds: true,
    },
  };
}
